import { useNavigate } from 'react-router-dom';
import { ClockIcon, PeopleIcon, ChecklistIcon } from '../activity-details/icons';
import { createActivityNavigationHandler } from '../../utils/navigation';
import {
  CAROUSEL_COLORS,
  CAROUSEL_TYPOGRAPHY,
  CAROUSEL_DIMENSIONS,
} from './constants';
import type { RecommendedActivity } from '../../types/recommendation.types';

// Import placeholder assets
import defaultActivityPlaceholder from '../../../assets/images/default-activity.jpeg';
import LikeCountBadge from '../activity-details/LikeCountBadge';


interface RecommendationCardProps {
  activity: RecommendedActivity;
}

/**
 * RecommendationCard Component
 *
 * Small activity card for category recommendations carousel.
 * Design spec: Figma node 2213:11746 (Card/Small component)
 *
 * Features:
 * - Fixed 312px width on tablet/desktop, 280px on mobile
 * - Activity image with like badge overlay
 * - Title, metadata (duration, play type, prep), description
 * - Skill recommendation container with bold keywords
 * - "VIEW FULL ACTIVITY" button
 */
export default function RecommendationCard({ activity }: RecommendationCardProps) {
  const navigate = useNavigate();
  const handleClick = createActivityNavigationHandler(activity.id, navigate);

  return (
    <div
      onClick={handleClick}
      className="flex-shrink-0 flex flex-col bg-white cursor-pointer w-[280px] sm:w-[312px]"
      style={{
        borderRadius: `${CAROUSEL_DIMENSIONS.cardBorderRadius}px`,
        padding: `${CAROUSEL_DIMENSIONS.cardPadding}px`,
        gap: `${CAROUSEL_DIMENSIONS.cardGap}px`,
        boxShadow: `0px 4px 16px 0px ${CAROUSEL_COLORS.cardShadow}`,
      }}
    >
      {/* Activity Image with Like Badge */}
      <div
        className="relative overflow-hidden flex-shrink-0"
        style={{
          height: `${CAROUSEL_DIMENSIONS.imageHeight}px`,
          borderRadius: `${CAROUSEL_DIMENSIONS.imageBorderRadius}px`,
        }}
      >
        <img
          src={activity.imageUrl ?? defaultActivityPlaceholder
          }
          alt={activity.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {(activity?.statistics?.thumbsUpCount ?? 0) > 0 && (
          <LikeCountBadge
            count={activity.statistics.thumbsUpCount}
            className="absolute bottom-2 right-2"
          />
        )}

        {/* Like Badge */}
        {/* {activity.statistics.thumbsUpCount > 0 && (
          <div
            className="absolute bottom-2 right-2 flex items-center"
            style={{
              backgroundColor: CAROUSEL_COLORS.likeBadgeBg,
              backdropFilter: 'blur(12px)',
              borderRadius: `${CAROUSEL_DIMENSIONS.likeBadgeBorderRadius}px`,
              padding: `${CAROUSEL_DIMENSIONS.likeBadgePaddingY}px ${CAROUSEL_DIMENSIONS.likeBadgePaddingX}px`,
              gap: `${CAROUSEL_DIMENSIONS.likeBadgeGap}px`,
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.25)',
            }}
          >
            <svg
              width={CAROUSEL_DIMENSIONS.likeBadgeIconSize}
              height={CAROUSEL_DIMENSIONS.likeBadgeIconSize}
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 12.25L6.125 11.4625C3.5 9.1 1.75 7.5125 1.75 5.6C1.75 4.0125 2.9125 2.85 4.5 2.85C5.3875 2.85 6.2375 3.2625 6.7 3.9125H7.3C7.7625 3.2625 8.6125 2.85 9.5 2.85C11.0875 2.85 12.25 4.0125 12.25 5.6C12.25 7.5125 10.5 9.1 7.875 11.4625L7 12.25Z"
                fill={CAROUSEL_COLORS.likeBadgeText}
              />
            </svg>
            <span
              style={{
                ...CAROUSEL_TYPOGRAPHY.likeBadge,
                color: CAROUSEL_COLORS.likeBadgeText,
                fontVariationSettings: "'opsz' 14",
              }}
            >
              {activity.statistics.thumbsUpCount} Parents liked this activity
            </span>
          </div>
        )} */}
      </div>

      {/* Title */}
      <h3
        className="line-clamp-1"
        style={{
          ...CAROUSEL_TYPOGRAPHY.cardTitle,
          color: CAROUSEL_COLORS.titleText,
          margin: 0,
          height: '20px',
          overflow: 'hidden',
          fontVariationSettings: "'opsz' 14",
        }}
      >
        {activity.title}
      </h3>

      {/* Metadata Grid */}
      <div className="grid grid-cols-2 gap-2">
        {/* Left Column */}
        <div className="flex flex-col gap-2">
          {/* Duration */}
          <div className="flex items-center gap-1">
            <ClockIcon size={14} color={CAROUSEL_COLORS.metadataText} />
            <span
              style={{
                ...CAROUSEL_TYPOGRAPHY.metadata,
                color: CAROUSEL_COLORS.metadataText,
                fontVariationSettings: "'opsz' 14",
              }}
            >
              {activity.durationText}
            </span>
          </div>

          {/* Play Type */}
          <div className="flex items-center gap-1">
            <PeopleIcon size={14} color={CAROUSEL_COLORS.metadataText} />
            <span
              style={{
                ...CAROUSEL_TYPOGRAPHY.metadata,
                color: CAROUSEL_COLORS.metadataText,
                fontVariationSettings: "'opsz' 14",
              }}
            >
              {activity.playTypeText}
            </span>
          </div>
        </div>

        {/* Right Column */}
        <div>
          {/* Prep Status */}
          <div className="flex items-center gap-1">
            <ChecklistIcon size={14} color={CAROUSEL_COLORS.metadataText} />
            <span
              style={{
                ...CAROUSEL_TYPOGRAPHY.metadata,
                color: CAROUSEL_COLORS.metadataText,
                fontVariationSettings: "'opsz' 14",
              }}
            >
              {activity.prepNeededText}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p
        className="line-clamp-2"
        style={{
          ...CAROUSEL_TYPOGRAPHY.description,
          color: CAROUSEL_COLORS.descriptionText,
          margin: 0,
          height: '28px',
          overflow: 'hidden',
          fontVariationSettings: "'opsz' 14",
        }}
      >
        {activity.description}
      </p>

      {/* Skill Recommendation Container */}
      {activity.whyItWorks && (
        <div
          style={{
            backgroundColor: CAROUSEL_COLORS.skillContainerBg,
            borderRadius: `${CAROUSEL_DIMENSIONS.skillContainerBorderRadius}px`,
            padding: `${CAROUSEL_DIMENSIONS.skillContainerPaddingY}px ${CAROUSEL_DIMENSIONS.skillContainerPaddingX}px`,
          }}
        >
          <p
            className="line-clamp-2"
            style={{
              ...CAROUSEL_TYPOGRAPHY.skillText,
              color: CAROUSEL_COLORS.skillTextPrimary,
              margin: 0,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {activity.whyItWorks.text}
          </p>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClick(); }}
        style={{
          height: `${CAROUSEL_DIMENSIONS.buttonHeight}px`,
          backgroundColor: activity.categoryColor || CAROUSEL_COLORS.buttonBackground,
          borderRadius: `${CAROUSEL_DIMENSIONS.buttonBorderRadius}px`,
          padding: `0 ${CAROUSEL_DIMENSIONS.buttonPaddingX}px`,
          border: 'none',
          cursor: 'pointer',
          ...CAROUSEL_TYPOGRAPHY.button,
          color: CAROUSEL_COLORS.buttonText,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        TRY THIS SPARK
      </button>
    </div>
  );
}
